/**
 * @fileoverview Password policy validation utilities
 * Creates dynamic Zod schemas and validation from organisation password policy
 */

import { z } from 'zod'

export interface PasswordPolicy {
  minLength: number
  maxLength: number
  minUppercase: number
  minLowercase: number
  minDigits: number
  minSpecialChars: number
  allowedSpecialChars?: string | null
  preventUsernameInPassword?: boolean
  preventCommonPasswords?: boolean
  preventSequentialChars?: boolean
  preventRepeatedChars?: boolean
  preventPersonalInfo?: boolean
  maxAgeDays?: number
  preventReuseCount?: number
  expiryWarningDays?: number
}

function countChars(password: string): { upper: number; lower: number; digits: number; special: number } {
  let upper = 0
  let lower = 0
  let digits = 0
  let special = 0
  for (const c of password) {
    if (c >= 'A' && c <= 'Z') upper++
    else if (c >= 'a' && c <= 'z') lower++
    else if (c >= '0' && c <= '9') digits++
    else special++
  }
  return { upper, lower, digits, special }
}

function hasSequentialChars(password: string, minLength: number = 3): boolean {
  const lower = password.toLowerCase()
  const sequences = ['0123456789', 'abcdefghijklmnopqrstuvwxyz', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm']
  for (const seq of sequences) {
    for (let i = 0; i <= seq.length - minLength; i++) {
      const sub = seq.substring(i, i + minLength)
      const rev = sub.split('').reverse().join('')
      if (lower.includes(sub) || lower.includes(rev)) return true
    }
  }
  return false
}

function hasRepeatedChars(password: string, minRepeat: number = 3): boolean {
  let count = 1
  for (let i = 1; i < password.length; i++) {
    if (password[i].toLowerCase() === password[i - 1].toLowerCase()) {
      count++
      if (count >= minRepeat) return true
    } else {
      count = 1
    }
  }
  return false
}

const COMMON_PASSWORDS = new Set([
  '123456',
  '123456789',
  'password',
  'qwerty',
  '111111',
  '12345',
  'abc123',
  'admin',
  'letmein',
  'welcome',
  'monkey',
  'dragon',
  'master',
  'sunshine',
  'princess',
  'football',
  'shadow',
  '1234',
])

/**
 * Validates password against policy. Returns error message or null if valid.
 */
export function validatePasswordAgainstPolicy(
  password: string,
  policy: PasswordPolicy,
  userContext?: { email?: string; firstName?: string; lastName?: string }
): string | null {
  if (password.length < policy.minLength) return `Password must be at least ${policy.minLength} characters long`

  if (password.length > policy.maxLength) return `Password must not exceed ${policy.maxLength} characters`

  const { upper, lower, digits, special } = countChars(password)

  if (policy.minUppercase > 0 && upper < policy.minUppercase)
    return `Password must contain at least ${policy.minUppercase} uppercase letter${policy.minUppercase > 1 ? 's' : ''}`

  if (policy.minLowercase > 0 && lower < policy.minLowercase)
    return `Password must contain at least ${policy.minLowercase} lowercase letter${policy.minLowercase > 1 ? 's' : ''}`

  if (policy.minDigits > 0 && digits < policy.minDigits)
    return `Password must contain at least ${policy.minDigits} digit${policy.minDigits > 1 ? 's' : ''}`

  if (policy.minSpecialChars > 0 && special < policy.minSpecialChars)
    return `Password must contain at least ${policy.minSpecialChars} special character${policy.minSpecialChars > 1 ? 's' : ''}`

  // allowedSpecialChars unset/empty → any special characters are allowed
  // allowedSpecialChars set → only those characters may appear as specials
  const allowedSpecial = policy.allowedSpecialChars?.trim() || null
  if (allowedSpecial && special > 0) {
    const specialCharSet = new Set(allowedSpecial.split(''))
    const invalidChars = [...new Set([...password].filter((c) => !/[A-Za-z0-9]/.test(c) && !specialCharSet.has(c)))]
    if (invalidChars.length > 0) return `Password contains invalid special characters. Allowed: ${allowedSpecial}`
  }

  if (policy.preventUsernameInPassword && userContext?.email) {
    const emailLocal = userContext.email.split('@')[0]
    if (emailLocal.length >= 3 && password.toLowerCase().includes(emailLocal.toLowerCase()))
      return 'Password must not contain your email address'
  }

  if (policy.preventPersonalInfo && userContext) {
    if (
      userContext.firstName &&
      userContext.firstName.length >= 2 &&
      password.toLowerCase().includes(userContext.firstName.toLowerCase())
    )
      return 'Password must not contain your personal information'

    if (
      userContext.lastName &&
      userContext.lastName.length >= 2 &&
      password.toLowerCase().includes(userContext.lastName.toLowerCase())
    )
      return 'Password must not contain your personal information'
  }

  if (policy.preventSequentialChars && hasSequentialChars(password))
    return 'Password must not contain sequential characters (e.g. 123, abc)'

  if (policy.preventRepeatedChars && hasRepeatedChars(password))
    return 'Password must not contain repeated characters (e.g. aaa, 111)'

  if (policy.preventCommonPasswords && COMMON_PASSWORDS.has(password.toLowerCase()))
    return 'Password is too common. Please choose a stronger password'

  return null
}

/**
 * Creates a Zod schema for password validation based on policy
 */
export function createPasswordSchema(
  policy: PasswordPolicy,
  userContext?: { email?: string; firstName?: string; lastName?: string }
) {
  return z.string().superRefine((val, ctx) => {
    const error = validatePasswordAgainstPolicy(val, policy, userContext)
    if (error)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: error,
      })
  })
}

/**
 * Returns human-readable password requirements hint for UI
 */
export function getPasswordRequirementsHint(policy: PasswordPolicy): string {
  const parts: string[] = []
  parts.push(`Between ${policy.minLength} and ${policy.maxLength} characters`)
  if (policy.minUppercase > 0) parts.push(`${policy.minUppercase} uppercase letter(s)`)
  if (policy.minLowercase > 0) parts.push(`${policy.minLowercase} lowercase letter(s)`)
  if (policy.minDigits > 0) parts.push(`${policy.minDigits} digit(s)`)
  if (policy.minSpecialChars > 0) parts.push(`${policy.minSpecialChars} special character(s)`)
  const allowedSpecial = policy.allowedSpecialChars?.trim()
  if (allowedSpecial) parts.push(`special characters only from: ${allowedSpecial}`)
  if (policy.preventUsernameInPassword) parts.push('no email/username')
  if (policy.preventCommonPasswords) parts.push('not a common password')
  if (policy.preventSequentialChars) parts.push('no sequential chars (123, abc)')
  if (policy.preventRepeatedChars) parts.push('no repeated chars (aaa, 111)')
  if (policy.preventPersonalInfo) parts.push('no personal info')
  return parts.join(', ')
}

/**
 * Generates a password that meets the given policy (client-side, for Generate Password button)
 */
export function generatePasswordFromPolicy(policy: PasswordPolicy): string {
  const specialChars = policy.allowedSpecialChars ?? '!@#$%^&*()_+-=[]{}|;:\'",.<>?/`~\\'
  const length = Math.max(policy.minLength, 12)
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lower = 'abcdefghijklmnopqrstuvwxyz'
  const digits = '0123456789'

  const pick = (str: string) => str[Math.floor(Math.random() * str.length)]

  const required = [
    ...Array(policy.minUppercase)
      .fill(0)
      .map(() => pick(upper)),
    ...Array(policy.minLowercase)
      .fill(0)
      .map(() => pick(lower)),
    ...Array(policy.minDigits)
      .fill(0)
      .map(() => pick(digits)),
    ...Array(policy.minSpecialChars)
      .fill(0)
      .map(() => pick(specialChars)),
  ]

  const allChars = upper + lower + digits + specialChars
  let password = required.join('')
  for (let i = password.length; i < length; i++) {
    password += pick(allChars)
  }
  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('')
    .slice(0, length)
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  maxLength: 32,
  minUppercase: 1,
  minLowercase: 1,
  minDigits: 1,
  minSpecialChars: 1,
  allowedSpecialChars: null,
  preventUsernameInPassword: false,
  preventCommonPasswords: true,
  preventSequentialChars: false,
  preventRepeatedChars: false,
  preventPersonalInfo: false,
  maxAgeDays: 0,
  preventReuseCount: 0,
  expiryWarningDays: 7,
}
